import * as React from "react";
import {useContext, useEffect, useMemo, useState} from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import CssBaseline from "@mui/material/CssBaseline";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import {useMediaQuery, useTheme} from "@mui/material";
import {IosShare, Save} from "@mui/icons-material";
import {Bot, Folder} from 'lucide-react';
import {getBacktestHistory, getTradingAlgos} from "../apis/junkyard";
import {
    GetBacktestHistoryRequest,
    GetTradingAlgosRequest,
    GetTradingAlgosResponse,
    StorableBacktestResultRequest,
    TradingAlgoStored
} from "../utils/typesTrading";
import {AuthContext} from "../components/AuthContext";
import AppTheme from "../themes/AppTheme";
import AppAppBar from "../components/AppAppBar";
import {TradingAlgoList} from "../components/TradingAlgoList";
import {AIAssistantPanel} from "../components/AIAssistantPanel";
import {CollapsibleSidebar} from "../components/CollapsibleSidebar";
import {BacktestResultsGraph} from "../components/BacktestResultsGraph";
import Footer from "../components/Footer";
import Stack from "@mui/material/Stack";
import {Tooltip} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ShareableBacktestLink from "../components/ShareableBacktestLink";
import {formatDateTime} from "../utils/datetime";

const formatDate = (value: Date | string | number | null | undefined): string => {
    if (value == null) {
        return "N/A";
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return "N/A";
    }

    return date.toLocaleDateString();
};

const formatPercent = (value: unknown): string => {
    if (typeof value !== "number") {
        return "N/A";
    }

    return `${(value * 100).toFixed(2)}%`;
};

const getSummaryValue = (backtest: StorableBacktestResultRequest, key: string): unknown => {
    return (backtest.backtest_result?.summary as Record<string, unknown> | undefined)?.[key];
};

const getBacktestLabel = (backtest: StorableBacktestResultRequest): string => {
    return `${formatDate(backtest.backtest_start_date)} - ${formatDate(backtest.backtest_end_date)}`;
};

export const BacktestHistoryPage: React.FC = () => {
    const authContext = useContext(AuthContext);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState<boolean>(false);
    const [mobileDrawerTab, setMobileDrawerTab] = useState<string>('strategies');
    const [tradingAlgos, setTradingAlgos] = React.useState<TradingAlgoStored[] | undefined>( undefined);
    const [numPages, setNumPages] = React.useState<number | null>(1);
    const [currentPage, setCurrentPage] = React.useState<number>(1);
    const [selectedTradingAlgo, setSelectedTradingAlgo] = React.useState<TradingAlgoStored | null>(null);
    const [backtests, setBacktests] = useState<StorableBacktestResultRequest[]>([]);
    const [selectedStrategyId, setSelectedStrategyId] = useState<string | null>(null);
    const [selectedBacktest, setSelectedBacktest] = useState<StorableBacktestResultRequest | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isShareDialogOpen, setIsShareDialogOpen] = useState<boolean>(false);

    async function getAlgos() {

        // Extract query parameters from URL
        const access_token = authContext.readAccessToken();
        console.debug("Auth context access token", access_token);

        if (access_token !== null) {
            try {
                const resp = await getTradingAlgos(
                    {
                        "access_token": access_token,
                        "page": currentPage,
                        "per_page": 90
                    } as GetTradingAlgosRequest
                )
                const response = resp as GetTradingAlgosResponse;
                console.debug("Response", response)

                setTradingAlgos(response.trading_algos || []);
                if (response.total_pages) {
                    setNumPages(response.total_pages);
                }
                console.debug("Got trading algorithms", tradingAlgos)
            } catch (error) {
                console.error(error);
            }

        }
    }

    useEffect(() => {
        getAlgos();
    }, [authContext, currentPage]);

    useEffect(() => {
        const loadBacktests = async () => {
            if (!authContext.isAuthenticated()) {
                setIsLoading(false);
                return;
            }
            if (!selectedTradingAlgo) {
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);

                const response = await getBacktestHistory({
                    access_token: authContext.readAccessToken(),
                    selected_trading_algo_id: selectedTradingAlgo.id
                } as GetBacktestHistoryRequest);

                if (response.success) {
                    const loadedBacktests = response.backtests || [];
                    setBacktests(loadedBacktests);
                    const sortedBacktests = [...loadedBacktests].sort((a, b) => {
                        const aTime = a.created_at_utc ? new Date(a.created_at_utc).getTime() : 0;
                        const bTime = b.created_at_utc ? new Date(b.created_at_utc).getTime() : 0;
                        return bTime - aTime;
                    });
                    const firstBacktest = sortedBacktests[0] || null;
                    setSelectedStrategyId(firstBacktest?.selected_trading_algo_id || null);
                    setSelectedBacktest(firstBacktest);
                    console.debug("Selected backtest", firstBacktest);
                }
            } catch (e) {
                console.error("Failed to load backtest history", e);
            } finally {
                setIsLoading(false);
            }
        };

        loadBacktests();
    }, [selectedTradingAlgo]);

    const onSelectedTradingAlgoChanged = (newTradingAlgo: TradingAlgoStored) => {
        setSelectedTradingAlgo(newTradingAlgo);
        if (isMobile) setMobileDrawerOpen(false);
    }

    const filteredBacktests = useMemo(() => {
        if (!selectedStrategyId) {
            return [];
        }

        return backtests
            .filter(backtest => backtest.selected_trading_algo_id === selectedStrategyId)
            .slice()
            .sort((a, b) => {
                const aTime = a.created_at_utc ? new Date(a.created_at_utc).getTime() : 0;
                const bTime = b.created_at_utc ? new Date(b.created_at_utc).getTime() : 0;
                return bTime - aTime;
            });
    }, [backtests, selectedStrategyId]);


    const handleShareBacktestResult = () => {
        setIsShareDialogOpen(true);
    }

    const handleCloseShareDialog = () => {
        setIsShareDialogOpen(false);
    }

    const renderMainContent = () => {
        if (!authContext.isAuthenticated() || isLoading || backtests.length === 0) {
            return (
                <Box sx={{display: "flex", alignItems: "center", justifyContent: "center", flex: 1, m: 2}}>
                    {isLoading
                        ? <CircularProgress />
                        : <Typography variant="h4" color="text.secondary">
                            {!authContext.isAuthenticated() ? "Sign in to view backtest history." : "No saved backtests yet."}
                          </Typography>
                    }
                </Box>
            );
        }

        if (selectedStrategyId === null) {
            return (
                <Box sx={{display: "flex", alignItems: "center", justifyContent: "center", flex: 1, m: 2}}>
                    <Typography variant="h4" color="text.secondary">Select a strategy</Typography>
                </Box>
            );
        }

        return (
            <Box
                sx={{
                    flex: 1,
                    ml: {xs: 0, md: 5},
                    pb: 10,
                    minHeight: "calc(100vh - 120px)",
                    minWidth: 0,
                    maxWidth: 1800,
                    transition: "margin-left 0.2s ease"
                }}
            >

                <Box
                    sx={{
                        flex: 1,
                        pb: 10,
                        minWidth: 0,
                    }}
                >
                    <Stack direction="column" spacing={1} sx={{ padding: 2, width: "100%" }}>
                    <Box
                        sx={{
                            p: 2,
                            pl: 0,
                            borderBottom: 1,
                            borderColor: "divider",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <Box>
                            <Typography variant="h6">Backtest History</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Click a row to view the results.
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{width: "100%"}}>
                    <TableContainer sx={{maxHeight: 340, pl: {xs: 0, md: 14}, pr: {xs: 0, md: 10}}}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Created At</TableCell>
                                    <TableCell>Period</TableCell>
                                    <TableCell align="right">Days</TableCell>
                                    <TableCell align="right">Total Return</TableCell>
                                    <TableCell align="right">CAGR</TableCell>
                                    <TableCell align="right">Max Drawdown</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredBacktests.map((backtest, index) => {
                                    const isSelected = selectedBacktest === backtest;
                                    const totalReturn = getSummaryValue(backtest, "Total Return");
                                    const cagr = getSummaryValue(backtest, "CAGR");
                                    const maxDrawdown = getSummaryValue(backtest, "Max Drawdown");

                                    return (
                                        <TableRow
                                            key={`${backtest.selected_trading_algo_id}-${backtest.backtest_start_date}-${backtest.backtest_end_date}-${index}`}
                                            hover
                                            selected={isSelected}
                                            onClick={() => setSelectedBacktest(backtest)}
                                            sx={{cursor: "pointer"}}
                                        >
                                            <TableCell>{backtest.created_at_utc ? formatDateTime(backtest.created_at_utc) : '--'}</TableCell>
                                            <TableCell>{getBacktestLabel(backtest)}</TableCell>
                                            <TableCell align="right">{backtest.backtest_dates.length}</TableCell>
                                            <TableCell align="right">{formatPercent(totalReturn)}</TableCell>
                                            <TableCell align="right">{formatPercent(cagr)}</TableCell>
                                            <TableCell align="right">{formatPercent(maxDrawdown)}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    </Box>
                    </Stack>
                </Box>

                <Box sx={{
                    justifyContent: 'right',
                    display: 'flex',
                    width: '100%',
                    borderBottom: 1,
                    borderColor: 'divider',
                    pb: 1,
                    mb: 2,
                }}>
                    <Stack direction={"row"} spacing={1}>
                        <Tooltip title={selectedBacktest?.unique_id ? "Share" : "Save the backtest first to share it"}>
                            <IconButton
                                onClick={handleShareBacktestResult}
                                size="small"
                                disabled={!selectedBacktest?.unique_id}
                            >
                                <IosShare />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Box>
                <Box sx={{
                    flexDirection: 'row',
                    height: "90%",
                }}>
                    {selectedBacktest !== null
                        && selectedBacktest.backtest_start_date !== null
                        && selectedBacktest.backtest_end_date !== null
                    && (
                        <BacktestResultsGraph
                            selectedTradingAlgo={selectedTradingAlgo}
                            backtestStartDate={selectedBacktest.backtest_start_date}
                            backtestEndDate={selectedBacktest.backtest_end_date}
                            backtestDates={selectedBacktest.backtest_dates}
                            stratPctReturns={selectedBacktest.strat_pct_returns}
                            bmarkPctReturns={selectedBacktest.bmark_pct_returns}
                            stratCashValues={selectedBacktest.strat_cash_values}
                            backtestResult={selectedBacktest.backtest_result}
                            onGoBack={() => {}}
                        />
                    )}
                </Box>
            </Box>
        );
    };

    const sidebarTabs = [
        {
            id: 'strategies',
            label: 'Strategies',
            icon: <Folder size={16}/>,
            content: (
                <TradingAlgoList
                    algos={tradingAlgos || []}
                    selectedAlgoChanged={onSelectedTradingAlgoChanged}
                    numPages={numPages}
                    onPageChanged={(page: number | null) =>  page !== null && setCurrentPage(page)}
                    onCreateNewAlgo={() => {}}
                />
            ),
        },
    ];

    const openMobileDrawer = (tabId: string) => {
        setMobileDrawerTab(tabId);
        setMobileDrawerOpen(true);
    };

    return (
        <AppTheme>
            <CssBaseline enableColorScheme />
            <AppAppBar />
            <Box sx={{ml: {xs: 1.5, md: 5}, mr: {xs: 1.5, md: 5}, mt: 12, mb: 5}}>
                {isMobile && (
                    <Box sx={{display: 'flex', gap: 1, mb: 1.5}}>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Folder size={16}/>}
                            onClick={() => openMobileDrawer('strategies')}
                        >
                            Strategies
                        </Button>
                    </Box>
                )}
                <Box sx={{display: "flex", justifyContent: "flex-start"}}>
                    {!isMobile && (
                        <CollapsibleSidebar tabs={sidebarTabs}/>
                    )}

                    {renderMainContent()}
                    {selectedBacktest && selectedBacktest.unique_id &&
                    <ShareableBacktestLink
                        open={isShareDialogOpen}
                        onClose={handleCloseShareDialog}
                        backtestUniqueId={selectedBacktest.unique_id}
                    />
                    }
                </Box>

                <Drawer
                    anchor="left"
                    open={isMobile && mobileDrawerOpen}
                    onClose={() => setMobileDrawerOpen(false)}
                >
                    {isMobile && (
                        <Box sx={{height: '100%', display: 'flex', flexDirection: 'column'}}>
                            <CollapsibleSidebar
                                key={mobileDrawerTab}
                                initialTabId={mobileDrawerTab}
                                tabs={sidebarTabs}
                                onCollapse={() => setMobileDrawerOpen(false)}
                            />
                        </Box>
                    )}
                </Drawer>
            </Box>
            <Footer />
        </AppTheme>
    );
};
